import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, ArrowDownToLine, ArrowUpFromLine, AlertTriangle, Thermometer, CalendarClock, ClipboardCheck } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, CartesianGrid } from "recharts";
import { api, fmtNum, fmtDate } from "@/lib/api";
import { StatCard, StatusBadge, PageHeader, ExpiryBadge } from "@/components/StatCard";
import { useAuth } from "@/context/AuthContext";
import { AiDailySummary } from "@/components/AiPanels";

const PIE_COLORS = ["#0284C7", "#0EA5E9", "#38BDF8", "#7DD3FC", "#0369A1", "#075985", "#0C4A6E", "#BAE6FD"];

export default function Dashboard() {
  const { user, can } = useAuth();
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)); }, []);

  if (!d) return <div className="text-slate-500" data-testid="dashboard-loading">Memuat ringkasan...</div>;

  return (
    <div data-testid="dashboard-page">
      <PageHeader title={`Halo, ${user.name.split(" ")[0]}`} subtitle="Ringkasan kondisi cold storage hari ini" />
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
        <StatCard title="Total Stok Tersedia" value={d.total_stock_mc} unit="MC" icon={Package} testId="kpi-total-stock" hint={`${d.total_batches} batch • ±${fmtNum(d.total_weight_kg)} kg${d.total_stock_kg_unit > 0 ? ` • ${fmtNum(d.total_stock_kg_unit)} kg batch per-kg` : ""}`} delay="rise-1" />
        <StatCard title="Stok Masuk Hari Ini" value={d.in_today_mc} unit="MC" icon={ArrowDownToLine} tone="emerald" testId="kpi-stock-in-today" delay="rise-2" />
        <StatCard title="Stok Keluar Hari Ini" value={d.out_today_mc} unit="MC" icon={ArrowUpFromLine} tone="amber" testId="kpi-stock-out-today" delay="rise-3" />
        <StatCard title="Peringatan Stok Rendah" value={d.low_stock_count} unit="batch" icon={AlertTriangle} tone="rose" testId="kpi-low-stock-alert" hint={`< ${d.low_stock_threshold} MC • ${d.out_of_stock_count} habis`} delay="rise-4" />
        <Link to="/inventory?expiry=warning" className="block"><StatCard title="Mendekati Kadaluarsa" value={d.expiring_count} unit="batch" icon={CalendarClock} tone={d.expired_count ? "rose" : "amber"} testId="kpi-expiring" hint={`≤ ${d.expiry_warn_days} hari • ${d.expired_count} sudah lewat`} delay="rise-4" /></Link>
      </div>

      {can("ai_use") && <AiDailySummary />}

      <Link to="/inventory?tab=opname" className={`mt-4 flex items-center justify-between gap-3 rounded-2xl border px-4 py-3 rise rise-2 ${!d.last_opname || d.last_opname.days_ago > 30 ? "border-amber-200 bg-amber-50/70 text-amber-900" : "border-sky-100 bg-white text-slate-700"}`} data-testid="dashboard-last-opname">
        <div className="flex items-center gap-2 text-sm font-semibold"><ClipboardCheck className="h-4 w-4" /> Opname terakhir: {d.last_opname ? `${d.last_opname.session_no} • ${fmtDate(d.last_opname.finished_at.slice(0, 10))} (${d.last_opname.days_ago === 0 ? "hari ini" : `${d.last_opname.days_ago} hari lalu`})` : "belum pernah dilakukan"}</div>
        <span className="text-xs font-semibold underline underline-offset-2">Buka Stock Opname</span>
      </Link>

      {d.expiring_items.length > 0 && (
        <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50/70 p-4 rise rise-2" data-testid="expiry-alert">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 font-semibold text-amber-900"><CalendarClock className="h-4 w-4" /> Peringatan masa simpan — {d.expiring_items.length} batch perlu segera dikeluarkan</div>
            <Link to="/inventory?expiry=warning" className="text-xs font-semibold text-amber-800 hover:underline" data-testid="link-expiring-inventory">Lihat di inventaris</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
            {d.expiring_items.map((i, idx) => (
              <div key={i.id} className="bg-white rounded-xl border border-amber-100 px-3 py-2.5 flex items-center justify-between gap-3" data-testid={`expiring-item-${idx}`}>
                <div className="min-w-0"><div className="text-sm font-semibold text-slate-800 truncate">{i.species_name} <span className="text-slate-400">• {i.size}</span></div><div className="text-[11px] text-slate-500 font-mono">{i.batch_code} • {fmtNum(i.quantity_mc)} MC • {i.location}</div></div>
                <ExpiryBadge item={i} />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mt-4">
        <div className="card-aqua p-5 xl:col-span-2 rise rise-2" data-testid="chart-trend">
          <div className="flex items-center justify-between mb-4">
            <div><h3 className="font-semibold text-slate-800">Tren Stok Masuk vs Keluar</h3><p className="text-xs text-slate-500">7 hari terakhir • satuan MC</p></div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={d.trend} barGap={4}>
              <CartesianGrid vertical={false} stroke="#E0F2FE" />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
              <Tooltip cursor={{ fill: "#F0F9FF" }} contentStyle={{ borderRadius: 12, border: "1px solid #BAE6FD" }} />
              <Legend iconType="circle" />
              <Bar dataKey="masuk" name="Masuk" fill="#0284C7" radius={[6, 6, 0, 0]} />
              <Bar dataKey="keluar" name="Keluar" fill="#7DD3FC" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-slate-900 rounded-2xl p-5 text-white rise rise-3" data-testid="widget-occupancy">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Kapasitas Cold Room</h3>
            <span className="inline-flex items-center gap-1 text-xs bg-sky-500/20 text-sky-300 px-2 py-1 rounded-full"><Thermometer className="h-3 w-3" /> -22.5°C</span>
          </div>
          <div className="mt-6 text-5xl font-extrabold font-mono tracking-tight" data-testid="occupancy-pct">{d.occupancy_pct}%</div>
          <div className="text-sm text-slate-400 mt-1">{fmtNum(d.total_stock_mc)} / {fmtNum(d.capacity_mc)} MC terisi</div>
          <div className="mt-4 h-3 rounded-full bg-slate-800 overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-sky-500 to-cyan-300 transition-[width] duration-700" style={{ width: `${Math.min(100, d.occupancy_pct)}%` }} />
          </div>
          <div className="mt-6">
            <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">Perlu perhatian</div>
            {d.low_stock_items.length === 0 && <div className="text-sm text-slate-300">Semua batch stok aman.</div>}
            <ul className="space-y-2">
              {d.low_stock_items.map((i) => (
                <li key={i.id} className="flex items-center justify-between text-sm">
                  <span className="truncate">{i.species_name} <span className="text-slate-400">• {i.size}</span></span>
                  <span className="font-mono text-amber-300 font-semibold">{i.quantity_mc} MC</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mt-4">
        <div className="card-aqua p-5 rise rise-3" data-testid="chart-composition">
          <h3 className="font-semibold text-slate-800">Komposisi Stok per Jenis</h3>
          <p className="text-xs text-slate-500 mb-2">satuan MC</p>
          {d.composition.length === 0 ? <div className="text-sm text-slate-500 py-10 text-center">Belum ada stok</div> : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={d.composition} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                  {d.composition.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => `${fmtNum(v)} MC`} contentStyle={{ borderRadius: 12, border: "1px solid #BAE6FD" }} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="card-aqua p-5 xl:col-span-2 rise rise-4" data-testid="recent-transactions">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-800">Transaksi Terbaru</h3>
            <Link to="/transactions" className="text-sm text-sky-700 font-semibold hover:underline" data-testid="link-all-transactions">Lihat semua</Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="table-head"><tr><th className="text-left px-3 py-2 rounded-l-lg">Tanggal</th><th className="text-left px-3 py-2">Tipe</th><th className="text-left px-3 py-2">Ikan / Size</th><th className="text-left px-3 py-2">Asal</th><th className="text-right px-3 py-2 rounded-r-lg">MC</th></tr></thead>
              <tbody>
                {d.recent_transactions.map((t) => (
                  <tr key={t.id} className="border-b border-sky-50 last:border-0">
                    <td className="px-3 py-2.5 text-slate-600">{fmtDate(t.date)}</td>
                    <td className="px-3 py-2.5">{t.type === "opname" ? <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold bg-sky-50 text-sky-700 border border-sky-200">Opname</span> : <StatusBadge status={t.type === "in" ? "tersedia" : "rendah"} />}{" "}<span className="sr-only">{t.type}</span></td>
                    <td className="px-3 py-2.5 font-medium text-slate-800">{t.species_name} <span className="text-slate-400">• {t.size}</span></td>
                    <td className="px-3 py-2.5 text-slate-600">{t.origin_name}</td>
                    <td className={`px-3 py-2.5 text-right font-mono font-semibold ${t.type === "in" ? "text-emerald-600" : t.type === "opname" ? "text-sky-600" : "text-amber-600"}`}>{t.type === "in" ? "+" : t.type === "opname" ? (t.quantity_mc > 0 ? "+" : "") : "-"}{fmtNum(t.quantity_mc)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
